import { useState, useEffect, useCallback } from 'react';
import DataService from '../XXX/DataService';

export interface Champion {
  id: string;
  name: string;
  imageUrl: string;
}

/**
 * Normalize a string for fuzzy champion search.
 * Strips apostrophes, periods, spaces, hyphens and lowercases.
 * e.g. "K'Sante" -> "ksante", "Kai'Sa" -> "kaisa", "Dr. Mundo" -> "drmundo"
 */
function normalizeForSearch(str: string): string {
  return str.toLowerCase().replace(/['\.\s-]/g, '');
}

/**
 * Hook that fetches the full champion list from Riot DDragon API
 * and provides a fuzzy search function.
 */
export const useChampions = () => {
  const [championsList, setChampionsList] = useState<Champion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChampions = async () => {
      try {
        setLoading(true);
        setError(null);

        const version = await DataService.fetchLatestVersion();

        const response = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch champions from Riot API');
        }

        const data = await response.json();

        const championsData: Champion[] = Object.values(data.data).map((champion: any) => ({
          id: champion.id,
          name: champion.name,
          imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${champion.id}.png`,
        }));

        // Add Practice Dummy
        championsData.push({
          id: 'DummyChampion',
          name: 'DummyChampion',
          imageUrl: 'https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/assets/characters/tft_trainingdummy/hud/icons2d/tft_trainingdummy_donothing.png',
        });

        setChampionsList(championsData);
      } catch (err) {
        console.error('Error loading champions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load champions');
      } finally {
        setLoading(false);
      }
    };

    loadChampions();
  }, []);

  /**
   * Search champions by query with fuzzy matching.
   * Prioritises names that start with the query, then alphabetical.
   */
  const searchChampions = useCallback((query: string): Champion[] => {
    if (!query.trim()) return [];

    const normalizedQuery = normalizeForSearch(query);

    return championsList
      .filter(champion => {
        const normalizedName = normalizeForSearch(champion.name);
        const normalizedId = normalizeForSearch(champion.id);
        return normalizedName.includes(normalizedQuery) || normalizedId.includes(normalizedQuery);
      })
      .sort((a, b) => {
        const normA = normalizeForSearch(a.name);
        const normB = normalizeForSearch(b.name);
        const aStartsWith = normA.startsWith(normalizedQuery);
        const bStartsWith = normB.startsWith(normalizedQuery);

        // 1. Prioritize names that start with the query
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // 2. Alphabetical
        return a.name.localeCompare(b.name);
      })
      .slice(0, 8);
  }, [championsList]);

  return {
    championsList,
    loading,
    error,
    searchChampions,
  };
};

export default useChampions;
